import {
  buildSourceZoneSections,
  sourceContextSchema,
} from 'features/libra/inspectionPlanning/sourceContext'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { GeckoMeasurementSystem } from 'types'

import { buildExecutionWorkPackSections } from './buildExecutionWorkPackSections'
import { WorkPackInputs } from './buildPlanningWorkPack'
import {
  PlanningWorkPack,
  WorkPackChapter,
  WorkPackDrawing,
  WorkPackSection,
} from './workPackTypes'

export function buildWorkPackChapters(
  input: WorkPackInputs,
  sections: WorkPackSection[],
  zones: PlanningWorkPack['zones'],
  drawings: WorkPackDrawing[]
): WorkPackChapter[] {
  const settings = input.exportConfig
  const captured = input.preparation.sourceContext
    ? unwrapFieldValue(input.fieldValues[input.preparation.sourceContext.field])
    : undefined
  const context =
    captured === undefined || captured === null
      ? undefined
      : sourceContextSchema.parse(captured)
  const capturedZoneSections = context
    ? buildSourceZoneSections(
        unwrapFieldValue(
          input.fieldValues[input.preparation.sourceZonesField]
        ) ?? [],
        input.preparation.sourceContext?.zoneFields
      )
    : []
  const procedures = [
    ...new Set(
      input.areas
        .flatMap((area) =>
          area.methodAssignments.map((method) => method.procedure.trim())
        )
        .filter(Boolean)
    ),
  ]
  const referenceRows = settings.references.map((reference) => ({
    label: reference.label,
    value:
      reference.value ??
      (reference.sourceField
        ? String(
            context?.fieldValues[reference.sourceField]?.value ?? 'Not captured'
          )
        : 'Not specified'),
  }))
  return [
    {
      id: 'references',
      title: settings.headings.references,
      sections: [
        {
          title: 'Codes and standards as recorded',
          rows: referenceRows.length
            ? referenceRows
            : [
                {
                  label: 'Status',
                  value: 'No code or standard references configured',
                },
              ],
        },
        {
          title: 'Inspection procedures',
          rows: procedures.length
            ? procedures.map((value, i) => ({
                label: `Procedure ${i + 1}`,
                value,
              }))
            : [{ label: 'Status', value: 'Not yet specified' }],
        },
      ],
    },
    {
      id: 'asset',
      title: settings.headings.asset,
      sections: [
        sections[1],
        {
          title: 'Source data basis',
          rows: [
            {
              label: 'Units',
              value:
                'Source data is reproduced in its recorded units. Planning and execution measurements use the selected report measurement system.',
            },
          ],
        },
        ...(context
          ? context.sections
          : [
              {
                title: 'Captured data gathering',
                rows: [
                  {
                    label: 'Status',
                    value:
                      'Full source context was not captured for this ticket. Available assessment and zone details follow.',
                  },
                ],
              },
            ]),
      ],
    },
    { id: 'plan', title: settings.headings.plan, sections: sections.slice(2) },
    ...zones.map((zone, index) => ({
      id: `zone-${index + 1}`,
      title: zone.title,
      sections: [
        ...zone.sections,
        ...capturedZoneSections.filter(
          (section) => section.id === `zone/${zone.sourceZoneId}`
        ),
      ],
    })),
    ...drawings.map((drawing, index) => ({
      id: `drawing-${index + 1}`,
      title: `Drawing ${index + 1}`,
      sections: [],
      drawing,
    })),
    {
      id: 'findings',
      title: settings.headings.findings,
      sections: buildExecutionWorkPackSections({
        execution: input.execution,
        fields: input.fieldValues,
        definitions: input.ontologyFields,
        system: input.measurementSystem ?? GeckoMeasurementSystem.Metric,
        documentNames: input.documentNames ?? {},
        performedField: settings.performedField,
      }),
    },
  ]
}
